import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'

import { useAtom } from 'jotai'
import { Divider as MuiDivider, Typography } from '@mui/material'
import { spacing } from '@mui/system'
import { useTranslation } from 'react-i18next'
import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'
import { NewsItemsGrid } from './NewsItemsGrid'
import { FluentWidthContainer } from '../components/FluentWidthContainer'
import LoadingIndicator from '../../components/LoadingIndicator'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import { NewsFilter } from './NewsFilter'
import { newsFiltersAtom } from '../../atoms/issueAtom'
import { useWatchListFilter } from '../../hooks/useWatchListFilter'
import { isAfter, subDays } from 'date-fns'

const Divider = styled(MuiDivider)(spacing)

function NewsView() {
    const { t } = useTranslation()
    const [newsItems, setNewsItems] = useState(null)
    const [filters] = useAtom(newsFiltersAtom)
    const { result: fetchedNews } = useFetchEntitiesWithCache('news')
    const { watchListFilter } = useWatchListFilter(filters.onlyInWatchLists.value)
    useLogUserEvent('news')

    useEffect(() => {
        if (!fetchedNews) return

        const now = new Date()
        const twoWeeksAgo = subDays(now, 15) // Including today, so 15 days back

        const filteredNewsItems = fetchedNews.filter(
            (newsItem) =>
                isAfter(new Date(newsItem.publicationDate), twoWeeksAgo) && // Only show news from the last 14 days
                watchListFilter(newsItem) &&
                (filters.issueTypes.value.includes(
                    newsItem.entityType === 'ABB Directed issue' ? 'Directed issue' : newsItem.entityType, // Handle ABB as Directed issue
                ) ||
                    (newsItem.entityType === 'Guarantor fee issue' && // When there's a Guarantor fee issue, match on linked issue type
                        filters.issueTypes.value.includes(newsItem.linkedIssue?.type))) &&
                (filters.sourceType.value.length === 0 || filters.sourceType.value.includes(newsItem.eventType)) &&
                !(newsItem.issueData?.type === 'IPO' && ['memorandum', 'prospectus'].includes(newsItem.eventType)),
        )

        setNewsItems(filteredNewsItems)
    }, [fetchedNews, filters, watchListFilter])

    return (
        <React.Fragment>
            <Helmet title={t('entities.news.plural')} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                }}
            >
                {t('entities.news.plural')}
            </Typography>
            <Divider my={6} />
            <FluentWidthContainer>
                <NewsFilter />
                <LoadingIndicator
                    loading={!newsItems}
                    label='Laddar nyhetsdata...'
                    labelSx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                />
                {newsItems && <NewsItemsGrid newsItems={newsItems} />}
            </FluentWidthContainer>
        </React.Fragment>
    )
}

export default NewsView
